import { Question } from '../../utils/mathProblems';
import { AnswerState } from '../../hooks/useGameLogic';
import { Difficulty } from '../../utils/mathProblems';

interface Props {
  question: Question;
  roundNumber: number;
  answerState: AnswerState;
  timeLeft: number | null;
  difficulty: Difficulty;
}

const DIFFICULTY_COLORS: Record<Difficulty, string> = {
  easy: 'bg-emerald-100 text-emerald-700',
  medium: 'bg-yellow-100 text-yellow-700',
  hard: 'bg-orange-100 text-orange-700',
  expert: 'bg-red-100 text-red-700',
};

export default function QuestionCard({ question, roundNumber, answerState, timeLeft, difficulty }: Props) {
  const timerPercent = timeLeft !== null
    ? difficulty === 'hard'
      ? (timeLeft / 20) * 100
      : (timeLeft / 12) * 100
    : null;

  const timerColor = timerPercent !== null
    ? timerPercent > 60 ? 'bg-green-500'
    : timerPercent > 30 ? 'bg-yellow-400'
    : 'bg-red-500'
    : '';

  return (
    <div
      className={`rounded-xl p-5 border-2 transition-colors ${
        answerState === 'correct'
          ? 'border-green-400 bg-green-50'
          : answerState === 'wrong'
          ? 'border-red-400 bg-red-50'
          : 'border-gray-200 bg-white'
      }`}
    >
      {/* Top row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 font-medium">Round {roundNumber}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${DIFFICULTY_COLORS[difficulty]}`}>
            {difficulty}
          </span>
        </div>
        {timeLeft !== null && (
          <div className="flex items-center gap-2">
            <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${timerColor}`}
                style={{ width: `${timerPercent}%` }}
              />
            </div>
            <span className={`text-sm font-bold tabular-nums ${timerPercent! <= 30 ? 'text-red-500' : 'text-gray-600'}`}>
              {timeLeft}s
            </span>
          </div>
        )}
      </div>

      {/* Question text */}
      <p className="text-gray-800 font-semibold text-base leading-relaxed mb-2">
        {question.question}
      </p>

      {/* Feedback */}
      {answerState !== 'idle' && (
        <div
          className={`flex items-start gap-2 mt-3 p-3 rounded-lg text-sm ${
            answerState === 'correct'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          <i className={`mt-0.5 flex-shrink-0 ${answerState === 'correct' ? 'ri-checkbox-circle-fill text-green-600' : 'ri-close-circle-fill text-red-600'}`} />
          <span>
            {answerState === 'correct'
              ? `Great shot! ${question.explanation}`
              : `Out of bounds. ${question.explanation}`}
          </span>
        </div>
      )}
    </div>
  );
}
